/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: APR.SLA_AUTHORITY
 *
 * Admin maintenance for two engine reference tables:
 *   apr_sla_policy      — SLA due window + breach action per config/level (E2/E5).
 *   apr_authority_limit — approval authority thresholds per user/role (E3).
 * Plain schema-aware CRUD via the gateway (admin RLS enforced server-side).
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, RefreshCw } from 'lucide-react';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import type { AuthSession } from '../../types/auth';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'APR.SLA_AUTHORITY';

interface ConfigRow { approval_config_id: number; internal_code: string; }
interface SlaRow {
  apr_sla_policy_id: number; approval_config_id: number; level: number | null;
  due_minutes: number; reminder_minutes: number | null; breach_action: string;
  use_business_calendar: 'Y' | 'N'; business_calendar_code: string | null; is_active: 'Y' | 'N';
}
interface AuthRow {
  apr_authority_limit_id: number; scope: string; scope_ref: string;
  entity_name: string | null; dimension: string; max_value: number; is_active: 'Y' | 'N';
}

const SLA_GRID_COLS: GridColumnDef[] = [
  { key: 'config',                 header: 'Config',     defaultWidth: 160 },
  { key: 'level',                  header: 'Level',      defaultWidth: 90  },
  { key: 'due_minutes',            header: 'Due min',    defaultWidth: 100 },
  { key: 'reminder_minutes',       header: 'Remind min', defaultWidth: 110 },
  { key: 'breach_action',          header: 'Breach',     defaultWidth: 140 },
  { key: 'use_business_calendar',  header: 'Cal',        defaultWidth: 70  },
  { key: 'business_calendar_code', header: 'Cal code',   defaultWidth: 120 },
  { key: 'is_active',              header: 'Active',     defaultWidth: 90  },
];

const AUTH_GRID_COLS: GridColumnDef[] = [
  { key: 'scope',       header: 'Scope',                    defaultWidth: 110 },
  { key: 'scope_ref',   header: 'Ref (user id / role code)', defaultWidth: 200 },
  { key: 'entity_name', header: 'Entity',                   defaultWidth: 160 },
  { key: 'dimension',   header: 'Dimension',                defaultWidth: 130 },
  { key: 'max_value',   header: 'Max value',                defaultWidth: 130 },
  { key: 'is_active',   header: 'Active',                   defaultWidth: 90  },
];

export interface ApprovalSlaAuthorityPageProps { session: AuthSession; }

export function ApprovalSlaAuthorityPage({ session }: ApprovalSlaAuthorityPageProps) {
  return (
    <PersonalizationProvider pageCode="APR.SLA_AUTHORITY" session={session}>
      <ApprovalSlaAuthorityPageInner session={session} />
    </PersonalizationProvider>
  );
}

function ApprovalSlaAuthorityPageInner({ session }: ApprovalSlaAuthorityPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [configs, setConfigs] = useState<ConfigRow[]>([]);
  const [sla, setSla] = useState<SlaRow[]>([]);
  const [auth, setAuth] = useState<AuthRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [c, s, a] = await Promise.all([
        apiDb.from('apr_config').select('approval_config_id, internal_code').order('internal_code'),
        apiDb.from('apr_sla_policy').select('apr_sla_policy_id, approval_config_id, level, due_minutes, reminder_minutes, breach_action, use_business_calendar, business_calendar_code, is_active').order('apr_sla_policy_id'),
        apiDb.from('apr_authority_limit').select('apr_authority_limit_id, scope, scope_ref, entity_name, dimension, max_value, is_active').order('apr_authority_limit_id'),
      ]);
      if (c.error) throw c.error; if (s.error) throw s.error; if (a.error) throw a.error;
      setConfigs((c.data ?? []) as ConfigRow[]);
      setSla((s.data ?? []) as SlaRow[]);
      setAuth((a.data ?? []) as AuthRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const addSla = async () => {
    setBusy(true); setError(null);
    try {
      if (configs.length === 0) throw new Error(t('apr_sla.err.no_config', 'Create an approval config first.'));
      const { error: e } = await routedInsert(session, 'apr_sla_policy', {
        approval_config_id: configs[0].approval_config_id, level: null, due_minutes: 480,
        breach_action: 'NOTIFY', use_business_calendar: 'N', is_active: 'Y',
      });
      if (e) throw e; await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const saveSla = async (r: SlaRow) => {
    const { error: e } = await routedUpdate(session, 'apr_sla_policy', 'apr_sla_policy_id', r.apr_sla_policy_id, {
      approval_config_id: r.approval_config_id, level: r.level, due_minutes: r.due_minutes,
      reminder_minutes: r.reminder_minutes, breach_action: r.breach_action,
      use_business_calendar: r.use_business_calendar, business_calendar_code: r.business_calendar_code || null,
      is_active: r.is_active,
    });
    if (e) setError(e.message);
  };
  const delSla = async (id: number) => { await routedDelete(session, 'apr_sla_policy', 'apr_sla_policy_id', id); setSla(x => x.filter(r => r.apr_sla_policy_id !== id)); };

  const addAuth = async () => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedInsert(session, 'apr_authority_limit', {
        scope: 'ROLE', scope_ref: '', entity_name: null, dimension: 'amount', max_value: 0, is_active: 'Y',
      });
      if (e) throw e; await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const saveAuth = async (r: AuthRow) => {
    const { error: e } = await routedUpdate(session, 'apr_authority_limit', 'apr_authority_limit_id', r.apr_authority_limit_id, {
      scope: r.scope, scope_ref: r.scope_ref, entity_name: r.entity_name || null,
      dimension: r.dimension, max_value: r.max_value, is_active: r.is_active,
    });
    if (e) setError(e.message);
  };
  const delAuth = async (id: number) => { await routedDelete(session, 'apr_authority_limit', 'apr_authority_limit_id', id); setAuth(x => x.filter(r => r.apr_authority_limit_id !== id)); };

  // §17.B5/B12: free-text search + Active facet (default Active-only) across both
  // is_active-bearing grids. Row identity (not array index) drives visibility so the
  // inline-edit handlers keep addressing the full sla/auth arrays by their index.
  const [filter, setFilter] = useState('');
  const [facets, setFacets] = useState<{ active: string[] }>({ active: ['Y'] });

  const facetOptions = {
    active: [
      { value: 'Y', label: t('common.boolean.active', 'Active') },
      { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
    ],
  };

  const anyFilterActive = facets.active.length > 0 || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ active: [] }); };

  const configCode = (id: number) => configs.find(c => c.approval_config_id === id)?.internal_code ?? '';

  const filteredSlaIds = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = sla.filter(r => {
      if (facets.active.length > 0 && !facets.active.includes(r.is_active)) return false;
      if (!q) return true;
      const hay = [configCode(r.approval_config_id), r.breach_action, r.business_calendar_code ?? '', r.level == null ? '' : String(r.level)]
        .join(' ').toLowerCase();
      return hay.includes(q);
    });
    return new Set(rows.map(r => r.apr_sla_policy_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sla, configs, filter, facets]);

  const filteredAuthIds = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = auth.filter(r => {
      if (facets.active.length > 0 && !facets.active.includes(r.is_active)) return false;
      if (!q) return true;
      const hay = [r.scope, r.scope_ref, r.entity_name ?? '', r.dimension].join(' ').toLowerCase();
      return hay.includes(q);
    });
    return new Set(rows.map(r => r.apr_authority_limit_id));
  }, [auth, filter, facets]);

  const inp = 'h-7 rounded border border-csm-border px-1 text-csm-sm';

  const detailSla  = useExpandedRows<number>();
  const detailAuth = useExpandedRows<number>();

  // §17.B13 related children (expand/contract). An SLA policy's parent is its
  // approval config, resolved to its code via configCode (the surrogate id rides in
  // `title`). Authority limits reference free-text scope_ref with no loaded parent
  // object, so that grid's control renders inert.
  const buildSlaGroups = (id: number): RelatedChildGroup[] => {
    const row = sla.find(r => r.apr_sla_policy_id === id);
    if (!row) return [];
    return [
      { key: 'config', title: t('apr_sla.related.config', 'Config'), items: [{ id: row.approval_config_id, label: configCode(row.approval_config_id), title: String(row.approval_config_id) }] },
    ];
  };
  const hasSlaRelated = (id: number) => buildSlaGroups(id).some(g => g.items.length > 0);
  // TODO: wire relations
  const buildAuthGroups = (_id: number): RelatedChildGroup[] => [];
  const hasAuthRelated = (id: number) => buildAuthGroups(id).some(g => g.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('apr_sla.title', 'Approval SLA & Authority')}
          description={t('apr_sla.list.description', 'SLA due windows and approval authority thresholds for the workflow engine.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('apr_sla.refresh', 'Refresh')}
            </button>
          }
        />

        {error && (
          <ErrorBanner
            message={t('apr_sla.load_failed', "Couldn't load SLA policies and authority limits.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('apr_sla.search_placeholder', 'Search…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter
              label={t('common.boolean.active', 'Active')}
              options={facetOptions.active}
              selected={facets.active}
              onChange={v => setFacets(f => ({ ...f, active: v }))}
            />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('apr_sla.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="overflow-auto p-3">
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('apr_sla.sla_policies', 'SLA policies')}</h2>
          {canAdd && (<button onClick={() => void addSla()} className="h-7 rounded border border-sidebar bg-sidebar px-2 text-csm-xs text-white">{t('apr_sla.add', '+ Add')}</button>)}
        </div>
        <PersonalizedGrid componentKey="sla_grid" columns={SLA_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={`mb-6 ${GRID_TABLE_CLASS}`}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1 text-left" title={t('apr_sla.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {sla.map((r, i) => filteredSlaIds.has(r.apr_sla_policy_id) && (
                  <Fragment key={r.apr_sla_policy_id}>
                  <tr className="border-t border-csm-border-soft">
                    <td className="px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detailSla.isOpen(r.apr_sla_policy_id)} hasChildren={hasSlaRelated(r.apr_sla_policy_id)} onToggle={() => detailSla.toggle(r.apr_sla_policy_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="px-1 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'config'                 && (
                          <div className="w-40">
                            <FilteredModalSingleSelect
                              label={t('apr_sla.field.config', 'Config')}
                              options={configs.map(c => ({ internal_code: String(c.approval_config_id), display_text: c.internal_code }))}
                              selectedCode={String(r.approval_config_id)}
                              onChange={(code) => {
                                const v = code ? Number(code) : r.approval_config_id;
                                const updated = { ...r, approval_config_id: v };
                                setSla(x => x.map((y, j) => j === i ? updated : y));
                                void saveSla(updated);
                              }}
                              allowClear={false}
                            />
                          </div>
                        )}
                        {col.key === 'level'                  && <input type="number" value={r.level ?? ''} placeholder={t('apr_sla.any', 'any')} onChange={e => setSla(x => x.map((y, j) => j === i ? { ...y, level: e.target.value === '' ? null : Number(e.target.value) } : y))} onBlur={() => void saveSla(sla[i])} className={inp + ' w-16'} />}
                        {col.key === 'due_minutes'            && <input type="number" value={r.due_minutes} onChange={e => setSla(x => x.map((y, j) => j === i ? { ...y, due_minutes: Number(e.target.value) } : y))} onBlur={() => void saveSla(sla[i])} className={inp + ' w-20'} />}
                        {col.key === 'reminder_minutes'       && <input type="number" value={r.reminder_minutes ?? ''} onChange={e => setSla(x => x.map((y, j) => j === i ? { ...y, reminder_minutes: e.target.value === '' ? null : Number(e.target.value) } : y))} onBlur={() => void saveSla(sla[i])} className={inp + ' w-20'} />}
                        {col.key === 'breach_action'          && <select value={r.breach_action} onChange={e => setSla(x => x.map((y, j) => j === i ? { ...y, breach_action: e.target.value } : y))} onBlur={() => void saveSla(sla[i])} className={inp}>{['NOTIFY','ESCALATE','AUTO_APPROVE','AUTO_REJECT'].map(o => <option key={o}>{o}</option>)}</select>}
                        {col.key === 'use_business_calendar'  && <input type="checkbox" checked={r.use_business_calendar === 'Y'} onChange={e => { setSla(x => x.map((y, j) => j === i ? { ...y, use_business_calendar: e.target.checked ? 'Y' : 'N' } : y)); }} onBlur={() => void saveSla(sla[i])} />}
                        {col.key === 'business_calendar_code' && <input type="text" value={r.business_calendar_code ?? ''} onChange={e => setSla(x => x.map((y, j) => j === i ? { ...y, business_calendar_code: e.target.value } : y))} onBlur={() => void saveSla(sla[i])} className={inp + ' w-24 font-mono'} />}
                        {col.key === 'is_active'              && <select value={r.is_active} onChange={e => setSla(x => x.map((y, j) => j === i ? { ...y, is_active: e.target.value as 'Y' | 'N' } : y))} onBlur={() => void saveSla(sla[i])} className={inp}><option>Y</option><option>N</option></select>}
                      </td>
                    ))}
                    <td className="px-1 py-1 text-right">{canDelete && (<button onClick={() => void delSla(r.apr_sla_policy_id)} className="text-csm-danger text-csm-2xs">{t('apr_sla.del', 'del')}</button>)}</td>
                  </tr>
                  {detailSla.isOpen(r.apr_sla_policy_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildSlaGroups(r.apr_sla_policy_id)} session={session} /></td></tr>
                  )}
                  </Fragment>
                ))}
                {filteredSlaIds.size === 0 && !busy && <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3"><EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={anyFilterActive ? t('apr_sla.no_match_sla', 'No SLA policies match the filters.') : t('apr_sla.empty_sla', 'No SLA policies.')} action={anyFilterActive ? undefined : canAdd && (<button onClick={() => void addSla()} className="h-7 rounded border border-sidebar bg-sidebar px-2 text-csm-xs text-white">{t('apr_sla.add', '+ Add')}</button>)} /></td></tr>}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>

        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('apr_sla.authority_limits', 'Authority limits')}</h2>
          {canAdd && (<button onClick={() => void addAuth()} className="h-7 rounded border border-sidebar bg-sidebar px-2 text-csm-xs text-white">{t('apr_sla.add', '+ Add')}</button>)}
        </div>
        <PersonalizedGrid componentKey="auth_grid" columns={AUTH_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1 text-left" title={t('apr_sla.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {auth.map((r, i) => filteredAuthIds.has(r.apr_authority_limit_id) && (
                  <Fragment key={r.apr_authority_limit_id}>
                  <tr className="border-t border-csm-border-soft">
                    <td className="px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detailAuth.isOpen(r.apr_authority_limit_id)} hasChildren={hasAuthRelated(r.apr_authority_limit_id)} onToggle={() => detailAuth.toggle(r.apr_authority_limit_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="px-1 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'scope'       && <select value={r.scope} onChange={e => setAuth(x => x.map((y, j) => j === i ? { ...y, scope: e.target.value } : y))} onBlur={() => void saveAuth(auth[i])} className={inp}><option>USER</option><option>ROLE</option></select>}
                        {col.key === 'scope_ref'   && <input type="text" value={r.scope_ref} onChange={e => setAuth(x => x.map((y, j) => j === i ? { ...y, scope_ref: e.target.value } : y))} onBlur={() => void saveAuth(auth[i])} className={inp + ' w-40 font-mono'} />}
                        {col.key === 'entity_name' && <input type="text" value={r.entity_name ?? ''} placeholder={t('apr_sla.all', 'all')} onChange={e => setAuth(x => x.map((y, j) => j === i ? { ...y, entity_name: e.target.value } : y))} onBlur={() => void saveAuth(auth[i])} className={inp + ' w-32 font-mono'} />}
                        {col.key === 'dimension'   && <input type="text" value={r.dimension} onChange={e => setAuth(x => x.map((y, j) => j === i ? { ...y, dimension: e.target.value } : y))} onBlur={() => void saveAuth(auth[i])} className={inp + ' w-24 font-mono'} />}
                        {col.key === 'max_value'   && <input type="number" value={r.max_value} onChange={e => setAuth(x => x.map((y, j) => j === i ? { ...y, max_value: Number(e.target.value) } : y))} onBlur={() => void saveAuth(auth[i])} className={inp + ' w-28'} />}
                        {col.key === 'is_active'   && <select value={r.is_active} onChange={e => setAuth(x => x.map((y, j) => j === i ? { ...y, is_active: e.target.value as 'Y' | 'N' } : y))} onBlur={() => void saveAuth(auth[i])} className={inp}><option>Y</option><option>N</option></select>}
                      </td>
                    ))}
                    <td className="px-1 py-1 text-right">{canDelete && (<button onClick={() => void delAuth(r.apr_authority_limit_id)} className="text-csm-danger text-csm-2xs">{t('apr_sla.del', 'del')}</button>)}</td>
                  </tr>
                  {detailAuth.isOpen(r.apr_authority_limit_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildAuthGroups(r.apr_authority_limit_id)} session={session} /></td></tr>
                  )}
                  </Fragment>
                ))}
                {filteredAuthIds.size === 0 && !busy && <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3"><EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={anyFilterActive ? t('apr_sla.no_match_auth', 'No authority limits match the filters.') : t('apr_sla.empty_auth', 'No authority limits.')} action={anyFilterActive ? undefined : canAdd && (<button onClick={() => void addAuth()} className="h-7 rounded border border-sidebar bg-sidebar px-2 text-csm-xs text-white">{t('apr_sla.add', '+ Add')}</button>)} /></td></tr>}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
    </div>
  );
}
